import useAxios from "axios-hooks";
import { useCallback } from "react";

export type ServerFolderData = {
  id: string;
  name: string;
  createdAt: string;
};

export type ServerUploadFileData = {
  id: string;
  folder: ServerFolderData;
};

const FOLDER_API_BASE = "/import-export-api/v1/private/folders";
const UI_IMPORT_FOLDER_NAME = "/ui_imports";

export const useImportFile = () => {
  const [getFolderState, getFolders] = useAxios<ServerFolderData[]>(
    { url: FOLDER_API_BASE, method: "GET" },
    { manual: true }
  );

  const [postFolderState, postFolder] = useAxios<ServerFolderData>(
    {
      url: FOLDER_API_BASE,
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    { manual: true }
  );

  const [putFileState, putFile] = useAxios<ServerUploadFileData>(
    {
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
    },
    { manual: true }
  );

  const checkImportFolderID = useCallback(async (): Promise<string> => {
    const { data: folders } = await getFolders();
    const existing = (folders ?? []).find((f) => f.name === UI_IMPORT_FOLDER_NAME);

    if (existing) return existing.id;

    const { data: created } = await postFolder({
      data: { name: UI_IMPORT_FOLDER_NAME },
    });

    return created.id;
  }, [getFolders, postFolder]);

  const importFile = useCallback(
    async (file: File): Promise<ServerUploadFileData> => {
      const folderId = await checkImportFolderID();

      const formData = new FormData();
      formData.append("file", file);

      const { data } = await putFile({
        url: `${FOLDER_API_BASE}/${folderId}/datafiles?suffixFileName=true`,
        data: formData,
      });

      return data;
    },
    [checkImportFolderID, putFile]
  );

  return {
    importFile,
    loading: getFolderState.loading || postFolderState.loading || putFileState.loading,
    error: getFolderState.error || postFolderState.error || putFileState.error,
  };
};
